import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Dispatch } from "../@types";
import { useSearchDispatchesMutation } from "../dispatchApiSlice";

interface OngoingTripDropdownMenuProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  error?: string;
}

interface TripOption {
  value: string;
  label: string;
}

const OngoingTripDropdownMenu: React.FC<OngoingTripDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Ongoing Trips",
  name = "partner",
  id = "partner",
  showLabel = true,
  error,
}) => {
  const [
    getDispatchHistory,
    { data: tripData, isLoading, isError, error: errorData },
  ] = useSearchDispatchesMutation();

  const partners = useMemo(() => {
    return tripData?.data.data || [];
  }, [tripData?.data.data]);

  useEffect(() => {
    getDispatchHistory({
      pageNumber: 0,
      pageSize: 100,
      tripStatus: "InTransit",
    });
  }, [getDispatchHistory]);

  const driversOptions: TripOption[] = useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading drivers" }];
    }

    return partners.map((trip: Dispatch) => ({
      value: trip.id,
      label: `${trip.fromLocation} - ${trip?.toLocation}`,
    }));
  }, [partners, isLoading, isError]);

  const handleValueChange = (option: TripOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: TripOption) => option.value;
  const getName = (option: TripOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<TripOption>
        name={name}
        id={id}
        label={showLabel ? "Ongoing Trips" : ""}
        options={driversOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading partners"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default OngoingTripDropdownMenu;
